import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import { stored, store, destroy } from '../lib/Utils'
import config from '../config'

axiosRetry(axios, { retryDelay: axiosRetry.exponentialDelay })

Vue.use(Vuex)

const instance = createAuthInterceptor(
  createAuthRefreshInterceptor(
    axios.create({
      baseURL: config.apiEndPoint,
      timeout: 5000
    })
  )
)

function createAuthInterceptor(instance) {
  function setAuthHeaders(config) {
    const accessToken = appStore.getters.accessToken
    if (accessToken) {
      config.headers.Authorization = `Bearer ${encodeURIComponent(accessToken)}` // No cyrillic, please
    } else {
      delete config.headers['Authorization']
    }
    return config
  }
  instance.interceptors.request.use(setAuthHeaders)
  return instance
}
function createAuthRefreshInterceptor(instance) {
  function refreshTokenCall(failedRequest) {
    const refreshToken = appStore.getters.refreshToken
    return axios
      .post(`${config.apiEndPoint}/auth/refresh`, { token: refreshToken })
      .then(tokenRefreshResponse => {
        const { accessToken, refreshToken } = tokenRefreshResponse.data.auth
        failedRequest.response.config.headers.Authorization = `Bearer ${accessToken}`
        appStore.commit('setAccessToken', accessToken)
        appStore.commit('setRefreshToken', refreshToken)
        return Promise.resolve()
      })
      .catch(error => {
        // Итак, нам не удалось обновить токен.
        // В этой точке наши токены более не действуют
        // Разлогиниваемся
        appStore.commit('message', { text: error.message, color: 'error' })
        appStore.commit('logout')
      })
  }

  const id = instance.interceptors.response.use(
    response => response,
    error => {
      if (
        !error.response ||
        error.response.status !== 401 ||
        !error.response.config.headers['Authorization']
      ) {
        return Promise.reject(error)
      }

      // Remove the interceptor to prevent a loop
      // in case token refresh also causes the 401
      instance.interceptors.response.eject(id)

      const refreshCall = refreshTokenCall(error)

      // Create interceptor that will bind all the others requests
      // until refreshTokenCall is resolved
      const requestQueueInterceptorId = instance.interceptors.request.use(
        request => refreshCall.then(() => request)
      )

      // When response code is 401 (Unauthorized), try to refresh the token.
      return refreshCall
        .then(() => {
          instance.interceptors.request.eject(requestQueueInterceptorId)
          return instance(error.response.config)
        })
        .catch(error => {
          instance.interceptors.request.eject(requestQueueInterceptorId)
          return Promise.reject(error)
        })
        .finally(() => createAuthRefreshInterceptor(instance))
    }
  )
  return instance
}

const state = {
  user: stored('user', {}),
  accessToken: stored('access-token'),
  refreshToken: stored('refresh-token'),
  messageText: '',
  messageColor: '',
  http: instance
}

const getters = {
  user: state => state.user,
  accessToken: state => state.accessToken,
  refreshToken: state => state.refreshToken,
  isAuthenticated: state =>
    Boolean(state.user.id) &&
    Boolean(state.accessToken) &&
    Boolean(state.refreshToken)
}

const mutations = {
  setUser: (state, user) => {
    state.user = user
    store('user', state.user)
  },
  setAccessToken: (state, accessToken) => {
    state.accessToken = accessToken
    store('access-token', state.accessToken)
  },
  setRefreshToken: (state, refreshToken) => {
    state.refreshToken = refreshToken
    store('refresh-token', state.refreshToken)
  },
  login: (state, { user, auth }) => {
    state.user = user
    const { accessToken, refreshToken } = auth
    state.accessToken = accessToken
    state.refreshToken = refreshToken
    store('user', state.user)
    store('access-token', state.accessToken)
    store('refresh-token', state.refreshToken)
  },
  logout: state => {
    state.user = {}
    state.accessToken = ''
    state.refreshToken = ''
    destroy('user')
    destroy('access-token')
    destroy('refresh-token')
  },
  message: (state, message) => {
    if (typeof message === 'string') {
      state.messageText = message
      state.messageColor = 'info'
    } else {
      const { text, color } = message
      state.messageText = text
      state.messageColor = color
    }
  }
}
const actions = {
  request({ state, commit }, payload) {
    return new Promise((resolve, reject) => {
      state
        .http(payload)
        .then(response => {
          // Successful outcome
          if (response.data) {
            if (response.data.message) {
              commit('message', response.data.message)
            }
            if (response.data.auth) {
              commit('setAccessToken', response.data.auth.accessToken)
              commit('setRefreshToken', response.data.auth.refreshToken)
            }
          }
          resolve(response.data)
        })
        .catch(error => {
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            commit('message', {
              text: error.response.data.message,
              color: 'error'
            })
          } else {
            commit('message', {
              text: error.message,
              color: 'error'
            })
          }
          reject(error)
        })
    })
  },
  login({ commit, dispatch }, payload) {
    const request = {
      url: 'auth/login',
      data: payload,
      method: 'post'
    }
    return dispatch('request', request).then(body => {
      commit('login', { user: body.data, auth: body.auth })
    })
  }
}
const appStore = new Vuex.Store({
  state,
  getters,
  mutations,
  actions
})

export default appStore
